import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatError } from '../lib/api';
import { LOGO_URL, SKETCH_PATTERNS } from '../lib/images';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{
          background: 'linear-gradient(160deg, #F2EFE9 0%, #E5E1D8 40%, #D4CBB3 100%)',
          backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`,
          backgroundSize: '300px',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}>
        <div className="text-center px-12 animate-fade-in relative z-10">
          <img src={LOGO_URL} alt="Today" className="w-40 h-40 mx-auto mb-8 rounded-full shadow-lg"
            style={{ border: '4px solid rgba(255,255,255,0.6)' }} />
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today</h1>
          <p className="text-base leading-relaxed max-w-sm mx-auto" style={{ color: 'var(--muted-fg)' }}>
            Start your journey to a healthier, more mindful life.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.waves}")`, backgroundRepeat: 'repeat-x', backgroundSize: '400px 100px', backgroundPosition: 'bottom' }} />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-slide-up">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Today" className="w-16 h-16 rounded-full object-cover mb-2 shadow-md" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today</span>
          </div>

          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Create Account</h2>
          <p className="mb-6" style={{ color: 'var(--muted-fg)' }}>Join thousands on their wellness journey</p>

          {/* Google Sign Up */}
          <button onClick={handleGoogleLogin} data-testid="google-register-btn"
            className="w-full py-3.5 rounded-lg font-semibold transition-all hover:shadow-md active:scale-[0.98] flex items-center justify-center gap-3 mb-5"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>
            <GoogleIcon />
            Sign up with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            <span className="text-xs" style={{ color: 'var(--muted-fg)' }}>or with email</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm" data-testid="register-error"
              style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text)' }}>Name</label>
              <input data-testid="register-name" type="text" value={name} onChange={e => setName(e.target.value)} required
                className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                placeholder="Your name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text)' }}>Email</label>
              <input data-testid="register-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text)' }}>Password</label>
              <div className="relative">
                <input data-testid="register-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 rounded-lg outline-none pr-12 transition-all"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  placeholder="At least 6 characters" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--muted-fg)' }}>
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button data-testid="register-submit" type="submit" disabled={loading}
              className="w-full py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'var(--text)', color: 'var(--bg)' }}>
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Account
            </button>
          </form>

          <p className="mt-6 text-center text-sm" style={{ color: 'var(--muted-fg)' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-semibold hover:underline" style={{ color: 'var(--primary)' }} data-testid="goto-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
